// Global access-error store (onboarding Phase 5 item 4). The AccessDeniedModal is mounted once at the App
// root, but until now NOTHING triggered it globally — only Goals.tsx called handleAccessError() by hand, so
// a 402/403 from any other query/mutation produced a dead shell instead of a message. This module-level
// store is fed by the queryClient's QueryCache/MutationCache onError (see lib/queryClient.ts), so EVERY
// 402/403 surfaces the modal regardless of which call failed. It lives outside React (the caches run
// outside the component tree) and is read into the root via useSyncExternalStore.

import { useSyncExternalStore } from "react";

export interface AccessErrorState {
  isOpen: boolean;
  errorCode: string;
  errorMessage: string;
  subscriptionStatus?: string;
  subscriptionTier?: string;
  expiresAt?: string;
  upgradeRequired?: boolean;
  premiumRequired?: boolean;
  supportContact?: boolean;
}

const CLOSED: AccessErrorState = { isOpen: false, errorCode: "", errorMessage: "" };

let state: AccessErrorState = CLOSED;
const listeners = new Set<() => void>();

function emit() {
  listeners.forEach((l) => l());
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  return () => { listeners.delete(cb); };
}

function getSnapshot(): AccessErrorState {
  return state;
}

/**
 * Feed an arbitrary thrown error (apiRequest/getQueryFn attach `.status` + `.data`). Acts only on the
 * server access-gate responses (402 billing, 403 hard-off); returns true when it surfaced the modal so the
 * caller can branch (e.g. re-evaluate the auth gate). Anything else is left for normal error handling.
 */
export function reportAccessError(error: unknown): boolean {
  const e = error as { status?: number; data?: Record<string, any> } | undefined;
  if (!e || (e.status !== 402 && e.status !== 403)) return false;
  const d = e.data ?? {};
  state = {
    isOpen: true,
    errorCode: d.code || "ACCESS_DENIED",
    errorMessage: d.error || "Zugang verweigert",
    subscriptionStatus: d.subscriptionStatus ?? d.accessState,
    subscriptionTier: d.subscriptionTier ?? d.currentTier,
    expiresAt: d.expiresAt,
    upgradeRequired: d.upgradeRequired,
    premiumRequired: d.premiumRequired,
    // The disabled-account case is a support matter, not a self-serve upgrade.
    supportContact: d.supportContact ?? d.code === "ACCOUNT_DISABLED",
  };
  emit();
  return true;
}

export function clearAccessError(): void {
  if (state.isOpen) {
    state = CLOSED;
    emit();
  }
}

/** Root hook: the App renders the single global AccessDeniedModal from this. */
export function useAccessError() {
  const errorState = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  return { errorState, clearError: clearAccessError };
}
